import { acquireTypeZodSchema } from "@@/shared/acquire/acquirable";
import { weekAvailabilityZodSchema } from "@@/shared/date-time/availability/week-availability";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { discountIdZodSchema } from "@@/shared/discounts/discount-id";
import { idZodSchema } from "@@/shared/id";
import { optionalGroupIdZodSchema } from "@@/shared/optionals/optional-group-id";
import { priceZodSchema } from "@@/shared/prices/price";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { tagsZodSchema } from "@@/shared/tags";
import {
    requiredTranslatableZodSchema,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { amountZodSchema } from "@@/shared/units-and-quantities/amount";
import { generateId } from "@@/utilities/generate-id";
import { z } from "zod";
import { productIdZodSchema } from "../product-id";

const productAddCommandZodSchema = z.object({
    _id: idZodSchema,
    _type: z.literal("PRODUCT_ADD_COMMAND"),
    _createdAt: isoAndUnixZodSchema,
    providerId: providerIdZodSchema,
    productId: productIdZodSchema,
    optionalGroupIds: z.array(optionalGroupIdZodSchema),
    title: requiredTranslatableZodSchema,
    subtitle: translatableZodSchema.optional(),
    kicker: translatableZodSchema.optional(),
    description: translatableZodSchema.optional(),
    articleNumber: z.string().optional(),
    price: priceZodSchema,
    imageUploads: z.array(z.any()),
    imageIds: z.array(storageItemImageIdZodSchema).optional(),
    stockTracked: z.boolean(),
    tags: tagsZodSchema.optional(),
    acquirableAs: acquireTypeZodSchema.readonly(),
    amount: amountZodSchema,
    discountIds: z.array(discountIdZodSchema),
    sectionIds: z.array(idZodSchema),
    availability: weekAvailabilityZodSchema.optional(),
    bookableType: z.enum(["TABLE_BOOKING", "RENTAL", "GENERIC"]).optional(),
});

type ProductAddCommand = z.infer<typeof productAddCommandZodSchema>;

const productAddCommandFactoryParamsZodSchema = productAddCommandZodSchema.omit(
    {
        _id: true,
        _type: true,
        _createdAt: true,
    },
);

type FactoryParams = z.infer<typeof productAddCommandFactoryParamsZodSchema>;

const productAddCommandFactory = (params: FactoryParams) => {
    const orderRejection: ProductAddCommand = {
        ...params,
        _id: generateId({ prefix: "product_add_command__" }),
        _type: "PRODUCT_ADD_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return orderRejection;
};

export {
    productAddCommandFactory,
    productAddCommandFactoryParamsZodSchema,
    productAddCommandZodSchema,
};
export type {
    ProductAddCommand,
    FactoryParams as ProductAddCommandFactoryParams,
};
